// src/stores/progressStore.js
//
// What you have done in Friendships Vibe, kept plainly: which modules you have
// finished (and when), and the insights you have saved. Nothing else - no
// points, levels, badges or streaks. Those were removed in October 2026
// (docs/REVIEW_2026-10.md, Decision 1); this replaces the gamification store
// that used to hold all of it, and utils/legacyProgress.js moves the old
// browser copy across once.
//
// Where it is kept:
//   - in this browser, under `friendships-vibe-progress` (zustand persist);
//   - while you are signed in, also in this app's database, so it follows you
//     to another device. The privacy pages say so ("your progress and the
//     insights you save from the dashboard are kept in this app's database").
//
// The database document is still users/{uid}/gamification/progress. The name
// is historical: it now holds only completedModules, moduleCompletedAt and
// savedInsights. Keeping the path means the security rules and "Delete All My
// Data" (services/DeleteMyDataService.js) stay exactly as they are; the first
// time a signed-in device loads, any old XP, level, streak or achievement
// fields in it are written away.
//
// Australian English throughout.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  PROGRESS_KEY,
  PROGRESS_VERSION,
  migrateLegacyProgress,
  normaliseModuleIds,
  mergeInsights,
  mergeCompletedAt,
  toRecord,
  hasLegacyGameFields,
} from '../utils/legacyProgress';

// Before the store below reads its own key: fold in, then remove, whatever the
// old gamification store left in this browser. Does nothing once that's done.
migrateLegacyProgress();

const emptyRecord = () => ({ completedModules: [], moduleCompletedAt: {}, savedInsights: [] });

const recordOf = (state) => ({
  completedModules: state.completedModules,
  moduleCompletedAt: state.moduleCompletedAt,
  savedInsights: state.savedInsights,
});

const sameRecord = (a, b) => JSON.stringify(toRecord(a)) === JSON.stringify(toRecord(b));

// ── The database copy ──────────────────────────────────────────────────────
//
// Loaded lazily, so the store (and anything that imports it) never pulls
// Firebase in at module scope.
async function database() {
  const [{ auth, db }, firestore] = await Promise.all([
    import('../services/config/firebase'),
    import('firebase/firestore'),
  ]);
  if (auth && typeof auth.authStateReady === 'function') {
    try { await auth.authStateReady(); } catch { /* treat as signed out */ }
  }
  return { uid: auth?.currentUser?.uid || null, db, firestore };
}

// Whether this session has read the database copy. Until it has, nothing is
// written to it: writing first could replace insights saved on another device
// with this browser's shorter list.
let loadPromise = null;
let loadedFromDatabase = false;

async function writeRecord(record) {
  const { uid, db, firestore } = await database();
  if (!uid || !db) return 'none';
  const { doc, setDoc } = firestore;
  // A full write, not a merge: the document holds the record and nothing
  // else, so no old game field can survive a save.
  await setDoc(doc(db, 'users', uid, 'gamification', 'progress'), {
    ...toRecord(record),
    updatedAt: new Date().toISOString(),
  });
  return 'saved';
}

// The sign-up page used to seed the account document (users/{uid}) with
// progress.totalXP: 0 and progress.currentLevel: 1. They were never read;
// they go the first time this device writes the record.
async function removeAccountGameFields(firestore, db, uid) {
  try {
    const { doc, getDoc, updateDoc, deleteField } = firestore;
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    const progress = snap.exists() ? snap.data()?.progress : null;
    if (progress && typeof progress === 'object' && ('totalXP' in progress || 'currentLevel' in progress)) {
      await updateDoc(ref, { 'progress.totalXP': deleteField(), 'progress.currentLevel': deleteField() });
    }
  } catch {
    // Not there, or offline: nothing reads these fields, so it can wait.
  }
}

async function pushToDatabase(get) {
  if (loadPromise) await loadPromise;
  if (!loadedFromDatabase) return;
  try {
    await writeRecord(recordOf(get()));
  } catch (error) {
    console.warn('[progress] could not save to the database:', error?.message);
  }
}

export const useProgressStore = create(
  persist(
    (set, get) => ({
      ...emptyRecord(),

      /** Record that a module has been finished. Once per module. */
      completeModule: (moduleId) => {
        const id = Number(moduleId);
        if (!Number.isInteger(id) || id <= 0) return;
        const state = get();
        if (state.completedModules.includes(id)) return;
        set({
          completedModules: normaliseModuleIds(state.completedModules, [id]),
          moduleCompletedAt: { ...state.moduleCompletedAt, [id]: new Date().toISOString() },
        });
        pushToDatabase(get);
      },

      /** Save an insight to your journal. Returns the saved insight. */
      saveInsight: (insight = {}) => {
        const saved = {
          id: Date.now().toString(),
          timestamp: new Date().toISOString(),
          ...insight,
        };
        set({ savedInsights: [saved, ...get().savedInsights] });
        pushToDatabase(get);
        return saved;
      },

      deleteInsight: (insightId) => {
        set({ savedInsights: get().savedInsights.filter((i) => i.id !== insightId) });
        pushToDatabase(get);
      },

      updateInsight: (insightId, updates = {}) => {
        set({
          savedInsights: get().savedInsights.map((i) =>
            i.id === insightId ? { ...i, ...updates, updatedAt: new Date().toISOString() } : i),
        });
        pushToDatabase(get);
      },

      /**
       * Read the database copy (signed-in only), merge it with this
       * browser's, and write the merged record back if they differ or the
       * document still has old game fields. Runs once per page load.
       */
      loadFromDatabase: () => {
        if (loadPromise) return loadPromise;
        loadPromise = (async () => {
          try {
            const { uid, db, firestore } = await database();
            if (!uid || !db) {
              // Signed out (or no database): this browser is the only copy.
              loadedFromDatabase = false;
              return 'local';
            }
            const { doc, getDoc, deleteDoc } = firestore;
            const snap = await getDoc(doc(db, 'users', uid, 'gamification', 'progress'));
            const remote = snap.exists() ? snap.data() : null;

            const local = recordOf(get());
            const merged = {
              completedModules: normaliseModuleIds(local.completedModules, remote?.completedModules),
              moduleCompletedAt: mergeCompletedAt(local.moduleCompletedAt, remote?.moduleCompletedAt),
              savedInsights: mergeInsights(local.savedInsights, remote?.savedInsights),
            };
            set(merged);
            loadedFromDatabase = true;

            const hasContent = merged.completedModules.length > 0 || merged.savedInsights.length > 0;
            const stale = remote ? (hasLegacyGameFields(remote) || !sameRecord(remote, merged)) : hasContent;
            if (stale) {
              await writeRecord(merged);
              await removeAccountGameFields(firestore, db, uid);
            }

            // The older top-level copy (gamification/{uid}) held only game
            // fields and a module list already folded in above; it goes with
            // the clean-up of the main document.
            if (remote && hasLegacyGameFields(remote)) {
              try { await deleteDoc(doc(db, 'gamification', uid)); } catch { /* none, or already gone */ }
            }
            return 'merged';
          } catch (error) {
            // Offline, or the read was refused: leave the database alone this
            // session rather than overwrite it with a partial copy.
            loadedFromDatabase = false;
            console.warn('[progress] could not read from the database:', error?.message);
            return 'failed';
          }
        })();
        return loadPromise;
      },

      /** Forget everything in memory (the caller removes the stored copies). */
      clearProgress: () => set(emptyRecord()),
    }),
    {
      name: PROGRESS_KEY,
      version: PROGRESS_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: recordOf,
      // Whatever is stored, read back only the record's three fields.
      merge: (persisted, current) => ({ ...current, ...toRecord(persisted || {}) }),
    }
  )
);

/** Called once when the app starts. */
export const initialiseProgress = () => useProgressStore.getState().loadFromDatabase();

export default useProgressStore;
